import { useState } from "react";
import { useGetInventorySpendKpi } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

// 2026-07-22 — Inventory Spend Dashboard.
//
// Layout (per the operator's design):
//  · Header row (title + period toggle + asOf caption)
//  · 4 KPI cards side-by-side (Complimentary, PayPerUse, Comp w/ Limit, Office Maint.)
//  · Left: Monthly Stock-In Spend by Bucket chart placeholder
//  · Right: Spend Matrix — categorised roll-up with MoM change

function formatINR(amount: number): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
}

type Period = "mtd" | "30d" | "3m" | "6m" | "9m" | "12m" | "ytd" | "custom";

const PERIODS: { key: Period; label: string; title?: string }[] = [
  { key: "mtd",    label: "MTD", title: "Month to date" },
  { key: "30d",    label: "30 D", title: "Last 30 days" },
  { key: "3m",     label: "3 M",  title: "Last 3 months (90 days)" },
  { key: "6m",     label: "6 M",  title: "Last 6 months (180 days)" },
  { key: "9m",     label: "9 M",  title: "Last 9 months (270 days)" },
  { key: "12m",    label: "12 M", title: "Last 12 months (365 days)" },
  { key: "ytd",    label: "YTD",  title: "Year to date (Jan 1 - today)" },
  { key: "custom", label: "Custom" },
];

interface Category {
  name: string;
  amount: number;
  priorAmount: number;
  changePct: number;
}

interface Bucket {
  id: string;
  displayName: string;
  color: string;
  description: string;
  totalSpend: number;
  priorSpend: number;
  changePct: number;
  shareOfTotal: number;
  ppuRevenue?: number | null;
  recoveredPct?: number | null;
  categories: Category[];
}

interface Attendance {
  uniqueMembers: number;
  memberDays: number;
  totalSessions: number;
}

export function SpendKpiDashboard() {
  const [period, setPeriod] = useState<Period>("mtd");
  const { data, isLoading } = useGetInventorySpendKpi({ period });

  const buckets = (data?.buckets ?? []) as Bucket[];
  const attendance = (data?.attendance ?? null) as Attendance | null;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Inventory · <span className="text-primary">Spend Dashboard</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Stock-in spend by usage model &amp; category · Pay-Per-Use recovery
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center rounded-md border p-0.5 gap-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                disabled={p.key === "custom"}
                title={p.key === "custom" ? "Coming soon" : p.title}
                className={cn(
                  "px-3 py-1 text-xs rounded",
                  period === p.key
                    ? "bg-primary text-primary-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {data?.asOfDate ? (
            <div className="text-xs text-muted-foreground">
              Data as of {format(new Date(data.asOfDate), "dd MMM yyyy")} · Source:
              Stock-In receipts (stocked) + Billing (PPU charges)
            </div>
          ) : null}
        </div>
      </div>

      {/* 4 KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {isLoading || buckets.length === 0
          ? Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))
          : buckets.map((b) => <BucketCard key={b.id} bucket={b} />)}
      </div>

      {/* Per-attendee-day breakdown — a dedicated card under the KPI grid
          so the ₹/head figures don't fight the category-share bars in the
          Complimentary card. */}
      {attendance && attendance.memberDays > 0 ? (
        <PerAttendeeCard buckets={buckets} attendance={attendance} />
      ) : null}

      {/* Charts + Spend Matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold mb-1">Monthly Stock-In Spend by Bucket</div>
            <div className="text-xs text-muted-foreground mb-4">
              Stacked view — watch Complimentary share vs the rest.
            </div>
            <div className="h-64 flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-md">
              Chart placeholder — recharts wiring in a follow-up commit.
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold mb-1">
              Spend Matrix — {(() => {
                const p = data?.period ?? period;
                const match = PERIODS.find((x) => x.key === p);
                return match?.label ?? "This Month";
              })()}
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              Category → sub-category, share of total, change vs prior period.
            </div>
            <SpendMatrix buckets={buckets} grandTotal={data?.grandTotal ?? 0} grandTotalChangePct={data?.grandTotalChangePct ?? 0} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="text-sm font-semibold mb-1">Pay-Per-Use: Stock-In Spend vs Revenue</div>
          <div className="text-xs text-muted-foreground mb-3">
            Are we recovering the PPU cost from members?
          </div>
          <PPUComparison
            bucket={buckets.find((b) => b.id === "pay-per-use") ?? null}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function BucketCard({ bucket }: { bucket: Bucket }) {
  // Per-head cost moved to its own dedicated card below the KPI row so it
  // doesn't fight the category-share bars for space.
  const isUp = bucket.changePct > 0;
  const isDown = bucket.changePct < 0;
  // For cost buckets: UP = bad (more spend). For PayPerUse: revenue view later.
  // Semantic color: cost-up shows red, cost-down shows green.
  const trendColor =
    isDown ? "text-emerald-700 bg-emerald-100"
    : isUp ? "text-red-700 bg-red-100"
    : "text-muted-foreground bg-muted";
  return (
    <Card className="overflow-hidden">
      <div className="h-1.5" style={{ backgroundColor: bucket.color }} />
      <CardContent className="p-4">
        <div
          className="text-xs font-bold uppercase tracking-wide"
          style={{ color: bucket.color }}
        >
          {bucket.displayName}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-2xl font-bold tabular-nums">{formatINR(bucket.totalSpend)}</div>
          {bucket.priorSpend > 0 || bucket.totalSpend > 0 ? (
            <span className={cn("flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-medium", trendColor)}>
              {isUp ? <TrendingUp className="h-3 w-3" /> : isDown ? <TrendingDown className="h-3 w-3" /> : null}
              {Math.abs(bucket.changePct).toFixed(0)}%
            </span>
          ) : null}
          {bucket.id === "pay-per-use" && bucket.recoveredPct != null ? (
            <span className="rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[11px] font-medium">
              {bucket.recoveredPct.toFixed(0)}% recovered
            </span>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {bucket.shareOfTotal.toFixed(0)}% of total spend · {bucket.description}
        </div>

        {/* Categories with proportional bar */}
        <div className="mt-4 space-y-2">
          {bucket.categories.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No spend this period.</div>
          ) : (
            bucket.categories.map((c) => {
              const pct = bucket.totalSpend > 0 ? (c.amount / bucket.totalSpend) * 100 : 0;
              return (
                <div key={c.name} className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2 text-xs">
                    <span className="truncate">{c.name}</span>
                    <span className="font-medium tabular-nums shrink-0">{formatINR(c.amount)}</span>
                  </div>
                  <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: bucket.color }}
                    />
                  </div>
                </div>
              );
            })
          )}
          {bucket.id === "pay-per-use" && bucket.ppuRevenue != null ? (
            <div className="mt-3 pt-3 border-t flex items-baseline justify-between text-xs">
              <span className="text-muted-foreground">PPU revenue this period</span>
              <span className="font-semibold text-emerald-700 tabular-nums">
                {formatINR(bucket.ppuRevenue)}
              </span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function PerAttendeeCard({
  buckets,
  attendance,
}: {
  buckets: Bucket[];
  attendance: Attendance;
}) {
  // Only cost buckets appear here — Pay-Per-Use isn't cost the space
  // absorbs (member pays), so it's excluded. Categories inside each
  // cost bucket are surfaced as rows.
  const costBuckets = buckets.filter((b) => b.id !== "pay-per-use");
  const rows: { label: string; amount: number; color: string; bold?: boolean }[] = [];
  for (const b of costBuckets) {
    // One header row for the bucket itself, then its categories.
    rows.push({ label: b.displayName, amount: b.totalSpend, color: b.color, bold: true });
    for (const c of b.categories) {
      rows.push({ label: c.name, amount: c.amount, color: b.color });
    }
  }
  const grandCost = costBuckets.reduce((s, b) => s + b.totalSpend, 0);
  const perHead = grandCost / attendance.memberDays;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <div>
            <div className="text-sm font-semibold">Per attendee-day cost</div>
            <div className="text-xs text-muted-foreground">
              Cost each attendance day absorbs across Complimentary, Comp-with-Limit and Office Maintenance.
            </div>
          </div>
          <div className="flex items-baseline gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Attendee days</div>
              <div className="text-lg font-semibold tabular-nums">{attendance.memberDays.toLocaleString("en-IN")}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Unique members</div>
              <div className="text-lg font-semibold tabular-nums">{attendance.uniqueMembers.toLocaleString("en-IN")}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Overall / head-day</div>
              <div className="text-lg font-semibold tabular-nums text-primary">{formatINR(perHead)}</div>
            </div>
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">Per attendee day</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const perHeadRow = attendance.memberDays > 0 ? r.amount / attendance.memberDays : 0;
                return (
                  <TableRow key={`${r.label}-${i}`} className={r.bold ? "bg-muted/40 font-medium" : undefined}>
                    <TableCell>
                      <div className={cn("flex items-center gap-2", r.bold ? "" : "pl-6")}>
                        {r.bold ? (
                          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                        ) : null}
                        <span className={r.bold ? "" : "text-muted-foreground"}>{r.label}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatINR(r.amount)}</TableCell>
                    <TableCell className="text-right tabular-nums" style={r.bold ? { color: r.color } : undefined}>
                      {formatINR(perHeadRow)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="border-t-2 font-semibold">
                <TableCell>All cost buckets</TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(grandCost)}</TableCell>
                <TableCell className="text-right tabular-nums text-primary">{formatINR(perHead)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function SpendMatrix({
  buckets,
  grandTotal,
  grandTotalChangePct,
}: {
  buckets: Bucket[];
  grandTotal: number;
  grandTotalChangePct: number;
}) {
  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">Spend</TableHead>
            <TableHead className="text-right">Share</TableHead>
            <TableHead className="text-right">MoM</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {buckets.map((bucket) => (
            <>
              <TableRow key={`bucket-${bucket.id}`} className="bg-muted/50 font-medium">
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: bucket.color }}
                    />
                    {bucket.displayName}
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(bucket.totalSpend)}</TableCell>
                <TableCell className="text-right tabular-nums">{bucket.shareOfTotal.toFixed(0)}%</TableCell>
                <TableCell className={cn("text-right tabular-nums", bucket.changePct > 0 ? "text-red-700" : bucket.changePct < 0 ? "text-emerald-700" : "")}>
                  {bucket.changePct > 0 ? "+" : ""}{bucket.changePct.toFixed(0)}%
                </TableCell>
              </TableRow>
              {bucket.categories.map((c) => (
                <TableRow key={`${bucket.id}-${c.name}`}>
                  <TableCell className="pl-9 text-muted-foreground">{c.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(c.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {grandTotal > 0 ? Math.round((c.amount / grandTotal) * 100) : 0}%
                  </TableCell>
                  <TableCell className={cn("text-right tabular-nums", c.changePct > 0 ? "text-red-700" : c.changePct < 0 ? "text-emerald-700" : "")}>
                    {c.changePct > 0 ? "+" : ""}{c.changePct.toFixed(0)}%
                  </TableCell>
                </TableRow>
              ))}
            </>
          ))}
          <TableRow className="font-semibold border-t-2">
            <TableCell>Total Stock-In Spend</TableCell>
            <TableCell className="text-right tabular-nums">{formatINR(grandTotal)}</TableCell>
            <TableCell className="text-right tabular-nums">100%</TableCell>
            <TableCell className={cn("text-right tabular-nums", grandTotalChangePct > 0 ? "text-red-700" : grandTotalChangePct < 0 ? "text-emerald-700" : "")}>
              {grandTotalChangePct > 0 ? "+" : ""}{grandTotalChangePct.toFixed(0)}%
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function PPUComparison({ bucket }: { bucket: Bucket | null }) {
  if (!bucket) {
    return (
      <div className="h-32 flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-md">
        No Pay-Per-Use data.
      </div>
    );
  }
  const cost = bucket.totalSpend;
  const revenue = bucket.ppuRevenue ?? 0;
  const recoveryColor = revenue >= cost ? "bg-emerald-500" : "bg-amber-500";
  const revenuePct = cost > 0 ? Math.min(200, (revenue / cost) * 100) : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-md border p-3">
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Stock-In Cost</div>
          <div className="text-xl font-semibold tabular-nums mt-1">{formatINR(cost)}</div>
        </div>
        <div className="rounded-md border p-3 bg-emerald-50/50">
          <div className="text-xs text-emerald-700 uppercase tracking-wide">PPU Revenue Collected</div>
          <div className="text-xl font-semibold tabular-nums mt-1 text-emerald-700">{formatINR(revenue)}</div>
        </div>
      </div>
      <div>
        <div className="flex items-baseline justify-between text-xs mb-1">
          <span className="text-muted-foreground">Recovery</span>
          <span className="font-medium tabular-nums">
            {bucket.recoveredPct?.toFixed(0) ?? 0}%
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all", recoveryColor)}
            style={{ width: `${Math.max(0, Math.min(100, revenuePct))}%` }}
          />
        </div>
      </div>
    </div>
  );
}
